import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@/shared/api/api-client';
import type { BlockSlotRequest } from '@/types/block-availability-api';
import {
  invalidateAfterBlockChange,
  postBlockSlotTo,
  type BlockSlotResponse,
} from './use-save-block-slot';

/**
 * `POST /api/Scheduling/BlockResourceSlot` — the DEVICE block route
 * (PROD-2510, Scheduling `8722bf64`).
 *
 * The twin of `useSaveBlockSlot`, and a SEPARATE hook rather than a flag on
 * it — exactly as `useDeviceAppointments` sits beside `useAppointments` and
 * `useDeviceCalendarSlots` beside `useCalendarSlots`, and exactly how the
 * backend mirrored it (Andrew, 2026-09-22: "lets mirror better because if
 * later they became different").
 *
 * The request body is IDENTICAL, and that is not an oversight:
 * `BlockResourceSlotCommand` DERIVES from `BlockSlotCommand` and adds no wire
 * field, because the body was already actor-generic — `actorId`, never
 * `practitionerId`. Underneath, one handler serves both commands. The route is
 * the only thing the two hooks disagree about, so the POST itself and the
 * cache invalidation are imported from the provider file rather than restated
 * here: that is the client's `BlockSlotCore`, and two copies of the
 * bare-`"Success"` parsing quirk is exactly the drift mirroring is supposed to
 * avoid.
 *
 * WHY THE SPLIT EXISTS, since an identical body makes it look redundant.
 *
 * The provider route built its SignalR broadcast with
 * `ReferenceType = "Provider"` and `GetProviderByIdInCache(actorId)`. A device
 * id matches no practitioner, PracticeManagement returned null from a unary
 * gRPC handler, null could not be serialised, and the request answered 500 —
 * AFTER the write had already committed, so every retry silently duplicated
 * the block. Nine stray device blocks accumulated on dev that way. Both routes
 * now refuse the other kind outright, BEFORE the write:
 *
 *   provider route + device actor → 400 "This actor is a device; block it
 *                                        through the device route."
 *   device route + practitioner   → 400 "A device block cannot name a
 *                                        practitioner."
 *
 * So this is not a nicety and the choice is not cosmetic: posting a device
 * block through `useSaveBlockSlot` is now a guaranteed 400. That is why
 * `BlockAvailabilityPanel` picks the hook from `state.actorKind` — the
 * discriminant that already decides the picker, the copy and the shift feed —
 * and from nothing else.
 *
 * Verified live on dev 2026-09-23: a device block through this route answers
 * 200 with a `BusyUnavailable` slot, and the same body through the provider
 * route answers 400 with the message above.
 */
export function useSaveBlockResourceSlot() {
  const qc = useQueryClient();
  return useMutation<BlockSlotResponse, ApiError, BlockSlotRequest>({
    // Its own key, for the same reason it is its own hook: a shared key would
    // put two different endpoints' in-flight state in one bucket.
    mutationKey: ['save-block-resource-slot'],
    mutationFn: (body) => postBlockSlotTo('/api/Scheduling/BlockResourceSlot', body),
    onSuccess: () => invalidateAfterBlockChange(qc),
  });
}
